import { Navigate } from 'react-router';
import ForgotPassword from '~/components/authentication/ForgotPassword';
import UserAuth from '~/components/authentication/UserAuth';
import { CloudflareChallenge } from '~/components/CloudflareChallenge';
import CustomSpinner from '~/commonComponents/CustomSpinner';
import { AdditionalOfficeInformation } from '~/components/registration/AdditionalOfficeInformation';
import { ContactInformation } from '~/components/registration/ContactInformation';
import { GeneralOfficeInformation } from '~/components/registration/GeneralOfficeInformation';
import { WorkerClassification as WorkerClassificationSignUp } from '~/components/registration/WorkerClassification';
import { RegistrationRedirect } from '~/components/AuthRedesign/RegistrationRedirect';
import { ForgotPasswordPage } from '~/components/AuthRedesign/forgotPassword';
import { LoginPage } from '~/components/AuthRedesign/login';
import { AuthShell } from '~/components/AuthRedesign/shared';
import {
  OfficeDetailsPage,
  OfficeSetupPage,
  SignUpLayout,
  SignUpPage,
  YourInfoPage,
} from '~/components/AuthRedesign/signUp';
import { WorkerTypePage } from '~/components/AuthRedesign/signUp/workerType';
import { SignUpRedesign } from '~/constants/ff.posthog';
import { useFeatureFlagVariantState } from '~/hooks/useFeatureFlagVariantState';
import { routes } from '..';
import { useAppRoutes } from '../hooks';

export const UnauthenticatedRoutes = () => {
  const signUpRedesignStatus = useFeatureFlagVariantState(SignUpRedesign, 'test');

  const legacyRoutes = [
    { path: routes.login, title: 'Login', element: <UserAuth /> },
    { path: routes.forgotPassword, title: 'Forgot Password', element: <ForgotPassword /> },
    {
      title: 'Registration - Contact Information',
      path: routes.contactInformation,
      element: <ContactInformation />,
    },
    {
      title: 'Registration - General Office Information',
      path: routes.generalOfficeInformation,
      element: <GeneralOfficeInformation />,
    },
    {
      title: 'Registration - Additional Office Information',
      path: routes.additionalOfficeInformation,
      element: <AdditionalOfficeInformation isRegistration />,
    },
    {
      title: 'Registration - Worker Classification',
      path: routes.workerClassification,
      element: <WorkerClassificationSignUp />,
    },
  ];

  const redesignRoutes = [
    {
      title: 'Auth Redesign Shell',
      element: <AuthShell />,
      children: [
        { path: routes.signUpRedesign.login, title: 'Login', element: <LoginPage /> },
        { path: routes.signUpRedesign.signUp, title: 'Sign Up', element: <SignUpPage /> },
        {
          title: 'Sign Up Steps',
          element: <SignUpLayout />,
          children: [
            {
              path: routes.signUpRedesign.yourInfo,
              title: 'Your Information',
              element: <YourInfoPage />,
            },
            {
              path: routes.signUpRedesign.officeDetails,
              title: 'Office Details',
              element: <OfficeDetailsPage />,
            },
            {
              path: routes.signUpRedesign.officeSetup,
              title: 'Office Setup',
              element: <OfficeSetupPage />,
            },
            {
              path: routes.signUpRedesign.workerType,
              title: 'Worker Type',
              element: <WorkerTypePage />,
            },
          ],
        },
      ],
    },
    {
      title: 'Auth Redesign Shell (Forgot Password)',
      element: <AuthShell variant="forgot-password" />,
      children: [
        {
          path: routes.signUpRedesign.forgotPassword,
          title: 'Forgot Password',
          element: <ForgotPasswordPage />,
        },
      ],
    },
    { path: '/registration/*', element: <RegistrationRedirect /> },
    { path: '/', element: <Navigate to={routes.signUpRedesign.login} replace /> },
  ];

  const sharedRoutes = [
    { path: routes.challenge, title: 'Security Verification', element: <CloudflareChallenge /> },
  ];

  const shouldUseSignUpRedesign = signUpRedesignStatus === 'enabled';
  const fallbackLoginPath = shouldUseSignUpRedesign ? routes.signUpRedesign.login : routes.login;

  // Viewport-sized wrapper so CustomSpinner (width/height: 100%) can center.
  const loadingElement = (
    <div style={{ height: '100vh', display: 'flex' }}>
      <CustomSpinner isLoading />
    </div>
  );

  const resolvedRoutes =
    signUpRedesignStatus === 'loading'
      ? [{ path: '*', element: loadingElement }]
      : [
          ...(shouldUseSignUpRedesign ? redesignRoutes : legacyRoutes),
          { path: '*', element: <Navigate to={fallbackLoginPath} replace /> },
        ];

  return useAppRoutes([...sharedRoutes, ...resolvedRoutes]);
};
